// ============================================================================
// Canonical Cattle Herds route contract
// ============================================================================
//
// CattleHerdsRouter owns one wildcard row, `/cattle/herds/*`: an empty suffix
// mounts the hub, every other suffix mounts the animal record. Those are two
// separately owned surfaces cut from ONE partition of the same raw space, so
// the partition lives here once instead of twice inside two presenters.
//
// The hub predicate is an exact pathname on purpose. `/cattle/herds/` is the
// wildcard BASE: its suffix is empty, so the generic surface classifier names
// the index for it, and a released index would then suppress recovery for a raw
// route this release never audited. Keeping the base out of the canonical hub
// form leaves it to the explicit route-recovery disposition.
//
// The record predicate accepts exactly one decoded-safe, unreserved segment.
// It decodes once and re-checks afterwards, so an encoded separator, NUL, line
// break, or dot segment cannot widen the released record capability.
//
// There is deliberately NO reserved segment beneath `/cattle/herds/` today:
// the cattle route table declares one wildcard row and no nested entry
// namespace, and no alias in the route map targets this space. That absence is
// derived from the live route tables by the static proof rather than assumed
// here, so a future addition fails loudly instead of quietly becoming a
// record id.
// ============================================================================

import {isCanonicalCommentAnchorHash} from './commentAnchorHash.ts';

export const CATTLE_HERDS_INDEX_PATHNAME = '/cattle/herds';
export const CATTLE_HERDS_DETAIL_PATHNAME_PREFIX = '/cattle/herds/';

type LocationLike = Readonly<{pathname?: unknown; search?: unknown; hash?: unknown}>;

function pathnameOf(location: unknown): unknown {
  if (typeof location === 'string') return location;
  if (!location || typeof location !== 'object' || Array.isArray(location)) return null;
  return (location as LocationLike).pathname;
}

/**
 * Characters that must never survive into a released record id, checked both
 * before and after a single decode. `?` and `#` are included because a caller
 * may hand this module a whole URL string as a "pathname"; React Router keeps
 * them separate, and neither presenter treats them as part of the id.
 */
function hasUnsafeRecordCharacter(value: string): boolean {
  return (
    value.includes('/') ||
    value.includes('\\') ||
    value.includes('\0') ||
    value.includes('\r') ||
    value.includes('\n') ||
    value.includes('?') ||
    value.includes('#')
  );
}

/** The single raw pathname that mounts the released Cattle Herds hub. */
export function isCanonicalCattleHerdsIndexPathname(pathname: unknown): pathname is string {
  return pathname === CATTLE_HERDS_INDEX_PATHNAME;
}

/**
 * CattleHerdsHub reads neither search nor hash — only CattleHerdsRouter reads
 * the location, and only its pathname. Either may therefore carry presentation
 * state without changing this hub claim, exactly as the router itself behaves.
 */
export function isCanonicalCattleHerdsIndexLocation(location: unknown): boolean {
  return isCanonicalCattleHerdsIndexPathname(pathnameOf(location));
}

/** Accept exactly one meaningful, decoded-safe raw record-id segment. */
export function isCanonicalCattleHerdsDetailPathname(pathname: unknown): pathname is string {
  if (typeof pathname !== 'string' || !pathname.startsWith(CATTLE_HERDS_DETAIL_PATHNAME_PREFIX)) {
    return false;
  }

  const rawId = pathname.slice(CATTLE_HERDS_DETAIL_PATHNAME_PREFIX.length);
  if (rawId === '' || hasUnsafeRecordCharacter(rawId)) return false;

  try {
    const id = decodeURIComponent(rawId);
    return id.trim() !== '' && id !== '.' && id !== '..' && !hasUnsafeRecordCharacter(id);
  } catch {
    return false;
  }
}

/**
 * React Router supplies pathname/search/hash separately. The record page keys
 * its resource on the pathname alone, so a bare string is accepted only when it
 * is plainly a pathname and a location object must carry no query or fragment.
 */
export function isCanonicalCattleHerdsDetailLocation(location: unknown): boolean {
  if (typeof location === 'string') return isCanonicalCattleHerdsDetailPathname(location);
  if (!location || typeof location !== 'object' || Array.isArray(location)) return false;

  const {pathname, search, hash} = location as LocationLike;
  return (
    (search === undefined || search === '') &&
    (hash === undefined || hash === '' || isCanonicalCommentAnchorHash(hash)) &&
    isCanonicalCattleHerdsDetailPathname(pathname)
  );
}
